import { useCallback, useEffect, useState } from 'react';
import { reelCredits } from 'fym-shared/src/music24/music24Plans';
import { useRegenerateReelMutation } from '../../../../../redux/api/fymAPI';
import { isSuccessfulResult } from '../../../../../redux/api/utils/isSuccessfulResult';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { aiReelStudioParams } from '../../studioRoute';
import { SerializedReel } from '../../types';
import {
  reelConflictCode,
  ReelConflictCode,
  reelRefusalCode,
  ReelRefusalCode,
  reelRefusalResetsAt,
} from './reelRefusal';
import { useVideoCreditGate } from '../../credits/useVideoCreditGate';

export type ReelSheet = 'approve' | 'reject' | null;

export interface ReelRefusal {
  code: ReelRefusalCode;
  resetsAt: string | null;
}

interface UseReelReviewProps {
  accountId: number;
  reel: SerializedReel;
  /** Loads the reel again after a 409. */
  refetch: () => void;
}

/**
 * The state of the preview (design c, d, e): the open sheet, the approve toast, a 409 alert, a
 * refused new reel, and "Make another" (regenerate) for a rejected reel with no child. "Make
 * another" costs one video credit: with a balance (plan plus packs) of 0 it opens the credit packs
 * at once and sends nothing (owner rule 2026-10-06).
 */
export function useReelReview({ accountId, reel, refetch }: UseReelReviewProps) {
  const router = useSMLRouter();
  const [sheet, setSheet] = useState<ReelSheet>(null);
  const [approvedCount, setApprovedCount] = useState<number | null>(null);
  const [conflict, setConflict] = useState<ReelConflictCode | null>(null);
  const [refusal, setRefusal] = useState<ReelRefusal | null>(null);
  const [regenerateReel, { isLoading: isMaking, error: regenerateError, reset: resetRegenerate }] =
    useRegenerateReelMutation();
  const { canSpend } = useVideoCreditGate(accountId);

  // Another reel on screen (a history click, a child) starts clean.
  useEffect(() => {
    setSheet(null);
    setApprovedCount(null);
    setConflict(null);
    setRefusal(null);
    resetRegenerate();
  }, [reel.uuid, resetRegenerate]);

  const songUuid = reel.catalogItemUuid;
  const openReel = useCallback(
    (uuid: string) =>
      router.push(
        SMLRoutes.promoteStudio,
        aiReelStudioParams(songUuid === null ? { reel: uuid } : { catalogItemUuid: songUuid, reel: uuid })
      ),
    [router, songUuid]
  );
  const closeSheet = useCallback(() => setSheet(null), []);
  const hideToast = useCallback(() => setApprovedCount(null), []);
  const closeRefusal = useCallback(() => setRefusal(null), []);

  const onApproved = useCallback((count: number) => {
    setSheet(null);
    setApprovedCount(count);
  }, []);

  const onConflict = useCallback(
    (code: ReelConflictCode) => {
      setSheet(null);
      setConflict(code);
      refetch();
    },
    [refetch]
  );

  const onRefused = useCallback((code: ReelRefusalCode, resetsAt: string | null) => {
    setSheet(null);
    setRefusal({ code, resetsAt });
  }, []);

  const onChild = useCallback(
    (childUuid: string) => {
      setSheet(null);
      openReel(childUuid);
    },
    [openReel]
  );

  const makeAnother = useCallback(async (): Promise<void> => {
    setConflict(null);
    setRefusal(null);
    if (!canSpend(reelCredits())) {
      return;
    }
    const result = await regenerateReel({ accountId, reelUuid: reel.uuid });
    if (isSuccessfulResult(result)) {
      openReel(result.data.child.uuid);
      return;
    }
    const code = reelRefusalCode(result.error);
    if (code !== null) {
      setRefusal({ code, resetsAt: reelRefusalResetsAt(result.error) });
      return;
    }
    const conflictCode = reelConflictCode(result.error);
    if (conflictCode !== null) {
      onConflict(conflictCode);
    }
  }, [accountId, canSpend, onConflict, openReel, reel.uuid, regenerateReel]);

  // A refusal and a 409 have their own notice; only another error shows the error alert.
  const makeAnotherError =
    regenerateError !== undefined &&
    reelRefusalCode(regenerateError) === null &&
    reelConflictCode(regenerateError) === null
      ? regenerateError
      : undefined;

  return {
    sheet,
    setSheet,
    closeSheet,
    approvedCount,
    hideToast,
    conflict,
    refusal,
    closeRefusal,
    onApproved,
    onConflict,
    onRefused,
    onChild,
    openReel,
    makeAnother,
    isMaking,
    makeAnotherError,
  };
}
